import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/features/auth'
import { SuperAdminSidebar } from '@/features/super-admin/components/SuperAdminSidebar'
import { SuperAdminShellContext } from '@/features/super-admin/super-admin-shell-context'
import { useEffect, useState, useMemo } from 'react'
import { Brain, ShieldAlert } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export const Route = createFileRoute('/super-admin')({
  component: SuperAdminLayout,
})

function SuperAdminLayout() {
  const { session, loading, signOut } = useAuth()
  const navigate = useNavigate()
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null)
  const [checkingAccess, setCheckingAccess] = useState(true)

  useEffect(() => {
    if (!loading && !session) {
      navigate({ to: '/login' })
    }
  }, [loading, session, navigate])

  useEffect(() => {
    async function checkSuperAdminAccess() {
      if (!session?.user?.id) {
        setCheckingAccess(false)
        return
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('auth_user_id', session.user.id)
          .single()

        if (error || !profile) {
          console.error('Error checking super admin access:', error)
          setIsSuperAdmin(false)
        } else {
          const { data: superAdmin, error: superAdminError } = await supabase
            .from('super_admins')
            .select('id')
            .eq('profile_id', profile.id)
            .maybeSingle()

          if (superAdminError) {
            console.error('Error checking super admin record:', superAdminError)
            setIsSuperAdmin(false)
          } else {
            setIsSuperAdmin(!!superAdmin)
          }
        }
      } catch {
        setIsSuperAdmin(false)
      } finally {
        setCheckingAccess(false)
      }
    }

    if (session) {
      checkSuperAdminAccess()
    }
  }, [session])

  const handleLogout = async () => {
    await signOut()
    navigate({ to: '/login' })
  }

  // Loading state
  if (loading || checkingAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl mb-4 shadow-md animate-pulse">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <p className="text-sm text-gray-500">Verificando permissões...</p>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!session) {
    return null
  }

  // Not a super admin
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Acesso Negado</h1>
          <p className="text-gray-500 mb-6">
            Você não possui permissão para acessar o painel de Super Admin. Esta área é restrita a
            administradores da plataforma.
          </p>
          <button
            onClick={() => navigate({ to: '/' })}
            className="inline-flex items-center justify-center px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    )
  }

  return <SuperAdminShell onLogout={handleLogout} />
}

function SuperAdminShell({ onLogout }: { onLogout: () => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const contextValue = useMemo(
    () => ({ onMenuToggle: () => setSidebarOpen((v) => !v) }),
    []
  )

  return (
    <SuperAdminShellContext.Provider value={contextValue}>
      <div className="super-admin-shell flex h-screen overflow-hidden bg-gray-100">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
            fixed inset-y-0 left-0 z-50 md:relative md:z-auto
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        >
          <SuperAdminSidebar
            onLogout={onLogout}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-auto min-w-0">
          <Outlet />
        </main>
      </div>
    </SuperAdminShellContext.Provider>
  )
}
